import axios, { AxiosResponse } from 'axios'
import {
  createAsyncThunk,
  isFulfilled,
  isPending,
  isRejected,
  isRejectedWithValue
} from '@reduxjs/toolkit'

import { RootState } from '..'
import {
  TApproveCurrentRoundPayload,
  TUnApproveCurrentRoundPayload,
  TDeleteTemporaryMediaPayload,
  TExtendedPostAttachment,
  TImportCanvaPayload,
  TNewPostSocialNotePayload,
  TPageFansPayload,
  TPostForApproval,
  TPostSocialNoteDeletedDataPayload,
  TPostSocialNoteUpdatedDataPayload,
  TPushAnswerByApproverPayload,
  TPushQuestionByContentCreatorPayload,
  TRequireApproverRevisionPayload,
  TStartNextApprovalRoundPayload,
  TStatusesCheckboxesPayload,
  TUpdatePostSchedulePayload,
  TUpdatePostSchedulesPayload
} from './newTypes'
import {
  ATTACHMENT_DESCRIPTION_TYPE,
  ATTACHMENT_TYPE,
  POST_TYPE,
  SOCIAL_NETWORK,
  TAttachmentExtraFields,
  TExtraFieldsPostSocial
} from '../../models/post'
import {
  setPostsForApprovalAction,
  toggleIsOpenAprrovalModalAction
} from '../modal/approvalPost/slice'
import { APPROVAL_LIST_TYPE } from '../application/types'

// Handle posts
export const savePostAsDraftThunk = createAsyncThunk(
  'managePost/savePostAsDraftThunk',
  async (data, { getState, dispatch, rejectWithValue, ...thunkApi }) => {
    const state = getState() as RootState
    const postData: TSavePostAsDraftData = {
      project_id: state.project.currentProject.id,
      social_networks: state.managePost.selectedSocials,
      type: state.managePost.selectedPostType,
      is_temporary: false
    }
    try {
      const responsesavePostAsDraft: AxiosResponse<any> = await axios.post('/posts', postData)
      return responsesavePostAsDraft.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const getPostThunk = createAsyncThunk(
  'managePost/getPostThunk',
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      if(window.location.pathname.includes('manage-post')){
        let params = window.location.pathname.split('/');
        if(params[0]==''){  
          let projectId =parseInt(params[1]);
          const responseUpdatePost: AxiosResponse<any> = await axios.get(`/posts/${projectId}/${id}`)
          // const responseUpdatePost: AxiosResponse<any> = await axios.get(`/posts/${id}`)
          return responseUpdatePost.data
        }else{
          const responseUpdatePost: AxiosResponse<any> = await axios.get(`/posts/${id}`)
          return responseUpdatePost.data
        }
      }else{
        const responseUpdatePost: AxiosResponse<any> = await axios.get(`/posts/${id}`)
        return responseUpdatePost.data
      }
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

const defineTypeOfAction = (attachment: TExtendedPostAttachment, social: SOCIAL_NETWORK) => {
  let actionType = []

  // attachment
  if (attachment.id) {
    if (attachment.toRemove) {
      actionType.push(MEDIA_ACTION_TYPE.DETACH_ATTACHMENT)
    } else if (attachment.toCopy) {
      actionType.push(MEDIA_ACTION_TYPE.COPY_ATTACHMENT)
    } else if (
      attachment.toDeatachMedia &&
      attachment.description === ATTACHMENT_DESCRIPTION_TYPE.CHILD_LINK
    ) {
      if (attachment.object_id) {
        actionType.push(MEDIA_ACTION_TYPE.UPDATE_LINK_ATTACHMENT)
      } else {
        actionType.push(MEDIA_ACTION_TYPE.DETACH_LINK_MEDIA)
      }
    } else if (attachment.toUpdate) {
      if (attachment.description !== ATTACHMENT_DESCRIPTION_TYPE.MEDIA) {
        if (!attachment.object_data?.canva_design_id) {
          actionType.push(MEDIA_ACTION_TYPE.UPDATE_MEDIA_ATTACHMENT_EXTRAFIELD)
        } else {
          actionType.push(MEDIA_ACTION_TYPE.UPDATE_LINK_ATTACHMENT)
        }
      } else if (attachment.object_data?.canva_design_id) {
        actionType.push(MEDIA_ACTION_TYPE.UPDATE_MEDIA)
      } else {
        actionType.push(MEDIA_ACTION_TYPE.UPDATE_MEDIA_ATTACHMENT_EXTRAFIELD)
      }
    }
  } else if (!attachment.toRemove) {
    if (attachment.description !== ATTACHMENT_DESCRIPTION_TYPE.MEDIA) {
      actionType.push(MEDIA_ACTION_TYPE.ATTACH_LINK)
    } else {
      actionType.push(MEDIA_ACTION_TYPE.ATTACH_MEDIA)
    }
  }

  // video attachment thumbnail. ONLY FACEBOOK
  if (social === SOCIAL_NETWORK.FACEBOOK_SOCIAL) {
    if (attachment.id) {
      if (attachment.toAddVideoTumbnail && !attachment.toUpdateVideoTumbnail) {
        actionType.push(MEDIA_ACTION_TYPE.ATTACH_THUMBNAIL)
      } else if (attachment.toUpdateVideoTumbnail) {
        actionType.push(MEDIA_ACTION_TYPE.UPDATE_THUMBNAIL)
      } else if (attachment.toRemoveVideoTumbnail) {
        actionType.push(MEDIA_ACTION_TYPE.DETACH_THUMBNAIL)
      }
    } else {
      if (attachment.toAddVideoTumbnail) {
        actionType.push(MEDIA_ACTION_TYPE.ATTACH_THUMBNAIL)
      }
    }
  }
  return actionType
}

const createAttachmentAction = (
  actionType: MEDIA_ACTION_TYPE,
  attachment: TExtendedPostAttachment
) => {
  let actionData = {}

  switch (actionType) {
    case MEDIA_ACTION_TYPE.COPY_ATTACHMENT: {
      actionData = { attachment_id: attachment.id, extra_fields: { ...attachment.extra_fields } }
      break
    }
    case MEDIA_ACTION_TYPE.ATTACH_MEDIA: {
      // TODO FBTAGS
      if (attachment.extra_fields) {
        actionData = {
          media_id: attachment.object_id,
          extra_fields: attachment.extra_fields
        }
      } else {
        actionData = { media_id: attachment.object_id }
      }
      break
    }
    case MEDIA_ACTION_TYPE.DETACH_ATTACHMENT: {
      actionData = { attachment_id: attachment.id }
      break
    }
    case MEDIA_ACTION_TYPE.UPDATE_MEDIA: {
      actionData = {
        attachment_id: attachment.id,
        media_id: attachment.object_id
      }
      break
    }
    case MEDIA_ACTION_TYPE.ATTACH_LINK: {
      actionData = {
        extra_fields: {
          link: attachment.extra_fields?.link || null,
          name: attachment.extra_fields?.name || null,
          description: attachment.extra_fields?.description || null,
          is_child_link: attachment.extra_fields?.is_child_link || false
        }
      }
      if (attachment.object_id) {
        actionData = {
          media_id: attachment.object_id,
          ...actionData
        }
      }
      break
    }
    case MEDIA_ACTION_TYPE.DETACH_LINK_MEDIA: {
      actionData = {
        attachment_id: attachment.id
      }
      break
    }
    case MEDIA_ACTION_TYPE.UPDATE_LINK_ATTACHMENT: {
      actionData = {
        attachment_id: attachment.id,
        extra_fields: attachment.extra_fields
      }

      if (attachment.object_id) {
        actionData = { ...actionData, media_id: attachment.object_id }
      }
      break
    }
    case MEDIA_ACTION_TYPE.UPDATE_MEDIA_ATTACHMENT_EXTRAFIELD: {
      actionData = {
        attachment_id: attachment.id,
        extra_fields: {
          ...attachment.extra_fields
        }
      }
      break
    }
    case MEDIA_ACTION_TYPE.ATTACH_THUMBNAIL: {
      actionData = {
        media_id: attachment.thumbnail_object_id
      }
      break
    }
    case MEDIA_ACTION_TYPE.UPDATE_THUMBNAIL: {
      actionData = {
        attachment_id: attachment.id,
        media_id: attachment.thumbnail_object_id
      }
      break
    }
    case MEDIA_ACTION_TYPE.DETACH_THUMBNAIL: {
      actionData = {
        attachment_id: attachment.id
      }
      break
    }
  }

  return { type: actionType, ...actionData }
}

export const updatePostSocialsThunk = createAsyncThunk(
  'managePost/updatePostSocialsThunk',
  async (data, { getState, dispatch, rejectWithValue, ...thunkApi }) => {
    const currentProjectId:any = window.location.pathname.split('/')[1];   
    const {
      managePost: { postSocials, post }
    } = getState() as RootState

    // I should map socials with id. Allright if I edit post social { facebook, insta }
    const dataToUpdateSocials: TUpdatePostSocialPayload = {
      post_id: post.id,
      project_id:currentProjectId,
      post_socials: postSocials.map((postSocial) => {
        const {
          id,
          message,
          first_comment,
          attachments,
          isRemoveAllAttachments,
          message_editor_state,
          extra_fields,
          hashtag,
          geolocation,
          shorten_link
        } = postSocial

        const media_actions: TMediaAction[] = []
        // To copy attachments from one to other socials.
        // At first we remove all other socials attachments
        // At second we duplicate all attachments from target social
        if (isRemoveAllAttachments) {
          media_actions.push({ type: MEDIA_ACTION_TYPE.DEATACH_ALL_ATTACHMENTS })
        }

        for (const attachment of attachments) {
          const actionTypes = defineTypeOfAction(attachment, postSocial.social_network)

          actionTypes.forEach((actionType) => {
            media_actions.push(createAttachmentAction(actionType, attachment))
          })
        }

        const dataToUpdate: TUpdatePostSocialData = {
          id,
          media_actions
        }

        dataToUpdate.message_editor_state = message_editor_state
        dataToUpdate.hashtag = hashtag
        dataToUpdate.geolocation = geolocation
        dataToUpdate.shorten_link = shorten_link

        if (typeof message === 'string') dataToUpdate.message = message
        if (typeof first_comment === 'string') dataToUpdate.first_comment = first_comment
        if (extra_fields && extra_fields.mentions && message_editor_state) {
          const editorDataRows = JSON.parse(message_editor_state)
          const editorMentions = Object.values(editorDataRows.entityMap).filter(
            (entity: any) => entity.type === 'mention'
          )
          const mentionsIds = editorMentions.map(
            (entityMention: any) => entityMention.data.mention.id
          )

          dataToUpdate.extra_fields = {
            ...extra_fields,
            mentions: extra_fields.mentions
              .filter((mention: any) => !!mention && mentionsIds.includes(mention.id))
              .map(({ id, mention, name, username }: any) => ({
                id,
                mention,
                name,
                username
              }))
          }
        } else {
          dataToUpdate.extra_fields = {
            ...extra_fields,
            mentions: []
          }
        }

        return dataToUpdate
      })
    }

    try {
      const responsesUpdateEachSocial = await axios.put(`/postssocials`, dataToUpdateSocials)
      return responsesUpdateEachSocial.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

// handle post schedule
export const schedulePostThunk = createAsyncThunk(
  'managePost/schedulePostThunk',
  async (data, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState
    const {
      latestSchedule: { date, time, is_autoposting },
      post: { id }
    } = state.managePost
    if (date && time && id) {
      const createScheduleData: TCreatePostSchedule = {
        date,
        time,
        post_id: id,
        is_autoposting
      }
      try {
        const responseCreatePost: AxiosResponse<any> = await axios.post(
          `/posts/schedules`,
          createScheduleData
        )
        return responseCreatePost.data
      } catch (err) {
        return rejectWithValue((err as any).response.data || err)
      }
    } else {
      rejectWithValue('Validation error')
    }
  }
)

export const updateScheduleThunk = createAsyncThunk(
  'managePost/updateScheduleThunk',
  async (data: TUpdatePostSchedulePayload, { rejectWithValue, dispatch }) => {
    try {
      const responseUpdatePost: AxiosResponse<any> = await axios.put(
        `/posts/schedules/${data.schedule_id}`,
        data
      )
      return responseUpdatePost.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const updateScheduleBeforeApprovalThunk = createAsyncThunk(
  'managePost/updateScheduleBeforeApprovalThunk',
  async (data: TUpdatePostSchedulePayload, { rejectWithValue, dispatch, getState }) => {
    const {
      managePost: { post, postSocials, latestSchedule: schedule }
    } = getState() as RootState
    const aprrovalPost: TPostForApproval = {
      ...post,
      schedule,
      postSocials
    }
    try {
      const updateScheduleThunkResponse = await dispatch(updateScheduleThunk(data))

      if (isFulfilled(updateScheduleThunkResponse)) {
        aprrovalPost.schedule = updateScheduleThunkResponse.payload
        dispatch(setPostsForApprovalAction([aprrovalPost]))
        dispatch(toggleIsOpenAprrovalModalAction())
      }
      return
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const updateSchedulesThunk = createAsyncThunk(
  'managePost/updateSchedulesThunk',
  async (data: TUpdatePostSchedulesPayload, { rejectWithValue, dispatch }) => {
    try {
      const responseUpdatePost: AxiosResponse<any> = await axios.put(`/posts/schedules`, data)
      return responseUpdatePost.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const deleteScheduleThunk = createAsyncThunk(
  'managePost/deleteScheduleThunk',
  async (schedule_id: number, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`/posts/schedules/${schedule_id}`)
      return schedule_id
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const addMediaToTemporaryMediasThunk = createAsyncThunk(
  'managePost/addMediaToTemporaryMediasThunk',
  async (data: FormData, { getState, rejectWithValue, dispatch }) => {
    const {
      project: {
        currentProject: { id }
      }
    } = getState() as RootState
    try {
      const responseAddMediaToPost: AxiosResponse<any> = await axios.post(
        `/projects/${id}/medias`,
        data
      )
      return responseAddMediaToPost.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const importTemporaryMediaFromCanva = createAsyncThunk(
  'managePost/importTemporaryMediaFromCanva',
  async (data: TImportCanvaPayload, { getState, rejectWithValue, dispatch }) => {
    const {
      project: {
        currentProject: { id }
      }
    } = getState() as RootState
    try {
      const responseAddMediaToPost: AxiosResponse<any> = await axios.post(
        `/projects/${id}/medias/import`,
        data
      )
      return responseAddMediaToPost.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const checkMediaRestrictionsThunk = createAsyncThunk(
  'managePost/checkMediaRestrictionsThunk',
  async (
    { media_id, social_options }: TCheckMediaRestrictionsThunk,
    { getState, rejectWithValue, dispatch }
  ) => {
    const {
      project: {
        currentProject: { id }
      }
    } = getState() as RootState
    try {
      const responseCheckMediaRestrictions: AxiosResponse<any> = await axios.post(
        `/projects/${id}/medias/${media_id}`,
        { social_options }
      )
      return responseCheckMediaRestrictions.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const checkMediaRestrictionsForCopyDataToSocialPostsThunk = createAsyncThunk(
  'managePost/checkMediaRestrictionsForCopyDataToSocialPostsThunk',
  async (social, { getState, rejectWithValue, dispatch }) => {
    const {
      managePost: { postSocials, selectedPostType, selectedSocial }
    } = getState() as RootState
    const postSocial = postSocials.find(
      (socialPost) => socialPost.social_network === selectedSocial
    )
    try {
      const actionResult: { [key in SOCIAL_NETWORK]: number[] } = {
        [SOCIAL_NETWORK.FACEBOOK_SOCIAL]: [],
        [SOCIAL_NETWORK.INSTAGRAM_SOCIAL]: [],
        [SOCIAL_NETWORK.LINKEDIN_SOCIAL]: [],
        [SOCIAL_NETWORK.TWITTER_SOCIAL]: []
      }

      if (postSocial) {
        const socialsOptions: TSocialOptionsToCheckRestrictions[] = postSocials
          .filter((socialPost) => socialPost.social_network !== selectedSocial)
          .map((socialPost) => ({
            social_network_name: socialPost.social_network,
            post_social_type: selectedPostType
          }))

        const promisesToCheckSocialsMedias = socialsOptions.map((socialOptions) =>
          postSocial.attachments
            .filter(({ toRemove, object_id }) => !toRemove && !!object_id)
            .map(({ object_id: media_id }) =>
              dispatch(
                checkMediaRestrictionsThunk({
                  media_id,
                  social_options: [socialOptions]
                })
              )
            )
        )

        await Promise.allSettled(
          promisesToCheckSocialsMedias.map(async (promisesToCheckMediasBySocial, index) => {
            const responseCheckMediasBySocial = await Promise.allSettled(
              promisesToCheckMediasBySocial
            )

            responseCheckMediasBySocial.map((checkedMediaResponse, index) => {
              if (
                checkedMediaResponse.status === 'fulfilled' &&
                isFulfilled(checkedMediaResponse.value)
              ) {
                const {
                  media_id,
                  social_options: [{ social_network_name: social }]
                } = checkedMediaResponse.value.meta.arg
                actionResult[social].push(media_id)
              }
            })
          })
        )
      }
      return actionResult
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const getTemporaryMediasThunk = createAsyncThunk(
  'managePost/getTemporaryMediasThunk',
  async (ids: TGetMediasFromTemporaryMediasPayload, { getState, rejectWithValue, dispatch }) => {
    const {
      project: {
        currentProject: { id }
      }
    } = getState() as RootState
    try {
      const responseAddMediaToPost: AxiosResponse<any> = await axios.get(
        `/projects/${id}/medias=${ids.join(',')}`
      )
      return responseAddMediaToPost
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const deleteMediaFromTemporaryMediasThunk = createAsyncThunk(
  'managePost/deleteMediaFromPostThunk',
  async (
    { project_id, media_id }: TDeleteTemporaryMediaPayload,
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const responseDeleteMediaFromPost = await axios.delete(
        `/projects/${project_id}/medias/${media_id}`
      )
      return responseDeleteMediaFromPost
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

// DEPRECATED
// export const attachMediaToPostSocialThunk = createAsyncThunk(
//   'managePost/attachMediaToPostSocialThunk',
//   async (
//     data: TAttachTemporaryMediaToPostSocialPayload,
//     { getState, rejectWithValue, dispatch }
//   ) => {
//     try {
//       const responsesOfAttachMedia = await axios.post(`/postssocials/medias`, data)
//       return responsesOfAttachMedia
//     } catch (err) {
//       return rejectWithValue((err as any).response.data || err)
//     }
//   }
// )

// DEPRECATED
// export const deleteMediaFromPostSocialThunk = createAsyncThunk(
//   'managePost/deleteMediaFromPostSocialThunk',
//   async (data: TDeleteMediaFromPostSocialThunkPayload, { rejectWithValue }) => {
//     try {
//       const responseAddMediaToPost: AxiosResponse<any> = await axios.post(
//         `/postssocials/media/delete`,
//         data
//       )
//       return responseAddMediaToPost.data
//     } catch (err) {
//       return rejectWithValue((err as any).response.data || err)
//     }
//   }
// )

// DEPRECATED
// export const updateMediaFromPostSocialThunk = createAsyncThunk(
//   'managePost/deleteMediaFromPostSocialThunk',
//   async ({ post_social_id, attachment_id, media_id }: TUpdateMediaFromPostSocialThunkPayload, { rejectWithValue }) => {
//     try {
//       const responseAddMediaToPost: AxiosResponse<any> = await axios.put(
//         `postssocials/${post_social_id}/attachments/${attachment_id}`,
//         { media_id }
//       )
//       return responseAddMediaToPost.data
//     } catch (err) {
//       return rejectWithValue((err as any).response.data || err)
//     }
//   }
// )

// DEPRECATED
// export const updateMediaForPost = createAsyncThunk(
//   'managePost/attachMediaToPostSocialThunk',
//   async (data, { getState, dispatch }) => {
//     const {
//       managePost: { postSocials }
//     } = getState() as RootState

//     const mediasToDeleteFromSocialPosts: TDeleteMediaFromPostSocialThunkPayload[] = []
//     const mediasToAddToSocialPosts: TAttachTemporaryMediaToPostSocialPayload[] = []
//     const mediasToUpdateSocialPosts: TUpdateMediaFromPostSocialThunkPayload[] = []

//     for (let { attachments, id: postSocialId } of postSocials) {
//       attachments.forEach((attachment) => {
//         if (!attachment.id && !attachment.toRemove) {
//           mediasToAddToSocialPosts.push({
//             media_id: attachment.object_id,
//             post_social_id: postSocialId
//           })
//         } else if (attachment.toRemove && attachment.id) {
//           mediasToDeleteFromSocialPosts.push({
//             attachment_id: attachment.id,
//             post_social_id: postSocialId
//           })
//         } else if (attachment.id && attachment.toUpdate) {
//           mediasToUpdateSocialPosts.push({
//             attachment_id: attachment.id,
//             post_social_id: postSocialId,
//             media_id: attachment.object_id
//           })
//         }
//       })
//     }

//     const responsesDeleteMediasPromises = await Promise.all(mediasToDeleteFromSocialPosts.map(async (data) =>
//       await dispatch(deleteMediaFromPostSocialThunk(data))
//     ))
//     const responsesAttachMediasPromises = await Promise.all(mediasToAddToSocialPosts.map(async (data) =>
//       await dispatch(attachMediaToPostSocialThunk(data))
//     ))
//     const responsesUpdateMediasPromises = await Promise.all(mediasToUpdateSocialPosts.map(async (data) =>
//       await dispatch(updateMediaFromPostSocialThunk(data))
//     ))
//   }
// )

export const getAllowedSocialTypesThunk = createAsyncThunk(
  'managePost/getAllowedSocialTypes',
  async (data: { social_networks: SOCIAL_NETWORK[] }, { rejectWithValue, dispatch }) => {
    try {
      const responseGetAllowedSocialTypes = await axios.post(`/posts/types/allowed`, data)
      return responseGetAllowedSocialTypes.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const savePostAsDraftAndScheduleThunk = createAsyncThunk(
  'managePost/savePostAsDraftAndScheduleThunk',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const saveDraftResponse = await dispatch(savePostAsDraftThunk())
      if (isFulfilled(saveDraftResponse)) {
        const schedulePostResponse = await dispatch(schedulePostThunk())
        isFulfilled(schedulePostResponse) && (await dispatch(updatePostSocialsThunk()))
        if(isRejected(schedulePostResponse)){
          return rejectWithValue(schedulePostResponse);
        }
      }
      return saveDraftResponse.payload.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const getPostApprovalInfoThunk = createAsyncThunk(
  'approval/getPostApprovalInfoThunk',
  async (post_id: number, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(`/posts/approvals/${post_id}`)
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const savePostSocialNoteThunk = createAsyncThunk(
  'approval/savePostSocialNoteThunk',
  async (data: TNewPostSocialNotePayload, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('message', data.message)
      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach((v) => {
          formData.append('attachments[]', v)
        })
      }
      const responseUserData: AxiosResponse<any> = await axios.post(
        `postssocials/${data.post_social_id}/notes`,
        formData
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const showAllPostSocialNoteThunk = createAsyncThunk(
  'approval/showAllPostSocialNoteThunk',
  async (post_social_id: number, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(
        `postssocials/${post_social_id}/notes`
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const deletePostSocialNoteThunk = createAsyncThunk(
  'approval/deletePostSocialNoteThunk',
  async (
    { post_social_id, note_id }: TPostSocialNoteDeletedDataPayload,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.delete(
        `postssocials/${post_social_id}/notes/${note_id}`
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const updateSocialPostNoteThunk = createAsyncThunk(
  'approval/updateSocialPostNoteThunk',
  async (
    { post_social_id, note_id, message, attachments }: TPostSocialNoteUpdatedDataPayload,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const formData = new FormData()
      formData.append('message', message)
      if (attachments && attachments.length > 0) {
        attachments.forEach((v) => {
          formData.append('attachments[]', v)
        })
      }
      const responseUserData: AxiosResponse<any> = await axios.post(
        `postssocials/${post_social_id}/notes/${note_id}`,
        formData
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const requireApproverRevisionThunk = createAsyncThunk(
  'approval/requireApproverRevisionThunk',
  async (
    { approval_id, checkboxes, message, attachments, type }: TRequireApproverRevisionPayload,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const formData = new FormData()
      formData.append('approval_id', approval_id.toString())
      if (message) formData.append('message', message)
      if (checkboxes) {
        checkboxes.forEach((v: { label?: string; is_checked?: boolean }, i) => {
          formData.append(`checkboxes[${i}][label]`, v.label ? v.label : '')
          formData.append(`checkboxes[${i}][is_checked]`, `${v.is_checked}`)
        })
      }
      if (attachments && attachments.length > 0) {
        attachments.forEach((v) => {
          formData.append('attachments[]', v)
        })
      }
      const url = type === APPROVAL_LIST_TYPE.APPROVED?`posts/approvals/unapprove-round`:`/posts/approvals/revision`
      const responseUserData: AxiosResponse<any> = await axios.post(url, formData)
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const addStatusesToCheckboxesInRevisionBlockThunk = createAsyncThunk(
  'approval/addStatusesToCheckboxesInRevisionBlockThunk',
  async (data: TStatusesCheckboxesPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.put(
        `posts/approvals/revision/checkboxes`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const pushQuestionByContentCreatorThunk = createAsyncThunk(
  'approval/pushQuestionByContentCreatorThunk',
  async (data: TPushQuestionByContentCreatorPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `/posts/approvals/revision/question`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const pushAnswerByApproverThunk = createAsyncThunk(
  'approval/pushAnswerByApproverThunk',
  async (data: TPushAnswerByApproverPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `/posts/approvals/revision/answer`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const startNextApprovalRoundThunk = createAsyncThunk(
  'approval/startNextApprovalRoundThunk',
  async (data: TStartNextApprovalRoundPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `/posts/approvals/next-round`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const approveCurrentRoundThunk = createAsyncThunk(
  'approval/approveCurrentRoundThunk',
  async (data: TApproveCurrentRoundPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `/posts/approvals/approve-round`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const unApproveCurrentRoundThunk = createAsyncThunk(
  'approval/unApproveCurrentRoundThunk',
  async (data: TUnApproveCurrentRoundPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `posts/approvals/unapprove-round`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const publishPostManuallyThunk = createAsyncThunk(
  'managePost/publishPostManuallyThunk',
  async (data, { dispatch, getState, rejectWithValue }) => {
    const post_id = (getState() as RootState).managePost.post.id
    try {
      const responsePublishingPost: AxiosResponse<any> = await axios.post(
        `/posts/publish/${post_id}`
      )
      return responsePublishingPost.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const adminPublishPostManuallyThunk = createAsyncThunk(
  'managePost/adminPublishPostManuallyThunk',
  async (data, { dispatch, getState, rejectWithValue }) => {
    const post_id = (getState() as RootState).managePost.post.id
    try {
      const responsePublishingPost: AxiosResponse<any> = await axios.post(
        `/posts/publish/admin/${post_id}`
      )
      return responsePublishingPost.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const getAvailableFansOnlineMetricsBySocialNetwork = createAsyncThunk(
  'insights/getAvailableFansOnlineMetricsBySocialNetwork',
  async ({ socialNetwork, projectId, date }: TPageFansPayload, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(
        `/insights/${socialNetwork}/project-fans-online-insights/${projectId}${
          date ? `?date=${date}` : ''
        }`
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)


export const hashtagSearchThunk = createAsyncThunk(
  'managePost/hashtagSearchThunk',
  async (data:{social:string,query: string}, { dispatch, getState, rejectWithValue }) => {
    const {
      project: {
        currentProject: { id: projectId }
      }
    } = getState() as RootState
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `/hashtag-name-list/${projectId}`,{
          q:data.query,
          social_network:data.social
        }
      )
      return responseUserData.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const getFacebooVideoTagsThunk = createAsyncThunk(
  'managePost/getFacebooVideoTagsThunk',
  async (query: string, { dispatch, getState, rejectWithValue }) => {
    const {
      project: {
        currentProject: { id: projectId }
      }
    } = getState() as RootState
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(
        `/projects/${projectId}/facebook/tags?q=${query}`
      )
      return responseUserData.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const getTwitterMentionsThunk = createAsyncThunk(
  'managePost/getTwitterMentionsThunk',
  async (query: string, { dispatch, getState, rejectWithValue }) => {
    const {
      project: {
        currentProject: { id: projectId }
      }
    } = getState() as RootState
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(
        `/projects/${projectId}/twitter/search?q=${query}`
      )
      return responseUserData.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const getFacebookMentionsThunk = createAsyncThunk(
  'managePost/getFacebookMentionsThunk',
  async (query: string, { dispatch, getState, rejectWithValue }) => {
    const {
      project: {
        currentProject: { id: projectId }
      }
    } = getState() as RootState
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(
        `/projects/${projectId}/facebook/search?q=${query}`
      )
      return responseUserData.data.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const getLinkedinMentionsThunk = createAsyncThunk(
  'managePost/getLinkedinMentionsThunk',
  async (query: string, { dispatch, getState, rejectWithValue }) => {
    const {
      project: {
        currentProject: { id: projectId }
      }
    } = getState() as RootState
    try {
      const responseUserData: AxiosResponse<any> = await axios.get(
        `/projects/${projectId}/linkedin/search?q=${query}`
      )
      return [responseUserData.data.data]
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const removeAttachmentThunk = createAsyncThunk(
  'approval/removeAttachmentThunk',
  async (data: { post_social_id: number; attachmentId: number }, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.delete(
        `postssocials/${data.post_social_id}/note-attachment/${data.attachmentId}`
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const reviseNotesThunk = createAsyncThunk(
  'managePost/reviseNotes',
  async (data: any, { rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `/posts/approvals/update-revision-comment`,
        data
      )
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

export const deleteNotesAttachment = createAsyncThunk(
  'managePost/deleteNotesAttachment',
  async (data: any, { rejectWithValue }) => {
    const { id } = data
    try {
      const responseUserData: AxiosResponse<any> = await axios.post(
        `posts/approvals/delete-attachment/${id}`
      )
      return true
    } catch (err) {
      return false
    }
  }
)

export const geolocationSearchThunk = createAsyncThunk(
  'managePost/geolocationSearchThunk',
  async (query:string, { getState, rejectWithValue, ...thunkApi})=>{
      const {
          project: {
            currentProject: { id: projectId }
          }
      } = getState() as RootState
      try{
          const response: AxiosResponse<any> = await axios.get(
              `/geolocation-list/${projectId}/${query}`
          )
          return response.data;
      } catch(err){
          return rejectWithValue((err as any).response.data || err)
      }
  }
)

export const getShortenLinkThunk = createAsyncThunk(
  'managePost/getShortenLinkThunk',
  async (query:string, { getState, rejectWithValue, ...thunkApi})=>{
    try{
      const response: AxiosResponse<any> = await axios.get(
        `/get-shorten-link${query ? `?url=${query}` : ''}`
      )
      return response.data;
    } catch(err){
        return rejectWithValue((err as any).response.data || err)
    }
  }
)

type TSavePostAsDraftData = {
  project_id: number
  type: string
  message?: string
  first_comment?: string
  social_networks?: string[]
  is_temporary: boolean
}

type TGetMediasFromTemporaryMediasPayload = number[]

type TDeleteMediaFromTemporaryMediasPayload = number

// type TAttachTemporaryMediaToPostSocialPayload = {
//   post_social_id: number
//   media_id: number
// }

// export type TDeleteMediaFromPostSocialThunkPayload = {
//   post_social_id: number
//   attachment_id: number
// }

// export type TUpdateMediaFromPostSocialThunkPayload = TDeleteMediaFromPostSocialThunkPayload & {
//   media_id: number
// }

type TCreatePostSchedule = {
  post_id: number
  date: string
  time: string
  is_autoposting: boolean
}

type TUpdatePostSocialData = {
  id: number
  message?: string | null
  first_comment?: string | null
  link?: string
  title?: string
  content_tags?: string
  extra_fields?: TExtraFieldsPostSocial
  media_actions: TMediaAction[]
  message_editor_state?: string | null
  hashtag?: any
  geolocation?: any
  shorten_link?: any
}

type TUpdatePostSocialPayload = {
  post_id: number,
  project_id:any,
  post_socials: TUpdatePostSocialData[]
}

type TMediaAction = {
  type: MEDIA_ACTION_TYPE
  attachment_id?: number
  media_id?: number
  extra_fields?: TAttachmentExtraFields
}

export enum MEDIA_ACTION_TYPE {
  ATTACH_MEDIA = 'attach-media',
  DETACH_ATTACHMENT = 'detach-attachment',
  UPDATE_MEDIA = 'update-attachment',
  COPY_ATTACHMENT = 'copy-attachment',
  ATTACH_LINK = 'attach-link',
  DETACH_LINK_MEDIA = 'detach-link-media',
  UPDATE_LINK_ATTACHMENT = 'update-link-attachment',
  DEATACH_ALL_ATTACHMENTS = 'detach-all-attachments',
  UPDATE_MEDIA_ATTACHMENT_EXTRAFIELD = 'update-attachment-extra-fields',
  ATTACH_THUMBNAIL = 'attach-thumbnail',
  DETACH_THUMBNAIL = 'detach-thumbnail',
  UPDATE_THUMBNAIL = 'update-thumbnail'
}

type TCheckMediaRestrictionsThunk = {
  media_id: number
  social_options: TSocialOptionsToCheckRestrictions[]
}

type TSocialOptionsToCheckRestrictions = {
  post_social_type: POST_TYPE
  social_network_name: SOCIAL_NETWORK
}
